import type { Instance, SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import type { ShopifyApiVersion } from "./connections/shopify/ShopifyApiVersion";
import type {
  IShopifyMetafieldFieldContributorConfiguration,
  ShopifyMetafieldFieldContributorID,
} from "./contributors/shopify/ShopifyFieldContributor";
import type { ProblemIdentifier, ProblemMessage } from "./problems/ProblemsList";
import type { FieldType } from "./type-system/FieldType";
import type { GlobalAction } from "./database/behaviour/GlobalAction";
import type { Role } from "./Role";
import type { Connection } from "./connections/Connection";
import type { AppSettings } from "./AppSettings";
import type { SourceFile } from "./code/SourceFile";
import type { DataModel } from "./database/DataModel";
import type { ModelField } from "./database/ModelField";
import type { AuthenticationMethod } from "./auth/AuthenticationMethod";
import type { Action } from "./database/behaviour/Action";

/** Thrown when the environment index is accessed for a state node tree that doesn't have one available, like a ReadOnlySchemaRoot */
export class UnavailableEnvironmentIndexError extends Error {}

export type GlobalActionIndexEntry = Readonly<{
  type: "GlobalActionIndexEntry" | GlobalAction["type"];
  key: string;
  apiIdentifier: string;
  namespace: string[];
  allProblems: ProblemsIndexEntry[];
  createdDate: number | Date;
  sourceFilePath: string;
}>;

export type RoleIndexEntry = Readonly<{
  type: "RoleIndexEntry" | Role["type"];
  key: string;
  name: string;
  allProblems: ProblemsIndexEntry[];
  createdDate: number | Date;
}>;

export type ConnectionIndexEntry = Readonly<{
  type: "ConnectionIndexEntry" | Connection["type"];
  key: string;
  name: string;
  allProblems: ProblemsIndexEntry[];
  createdDate: number | Date;
}>;

export type ShopifyConnectionIndexEntry = Readonly<{
  type: "ShopifyConnectionIndexEntry";
  key: string;
  name: string;
  allProblems: ProblemsIndexEntry[];
  createdDate: number | Date;
  shopifyApiVersion: ShopifyApiVersion;
  requestedScopes: string[];
  normalizedScopes: string[];
  enabledModelKeys: string[];
  appConfigs: { key: string; contentHash: number }[];
}>;

export type AppSettingsIndexEntry = Readonly<{
  type: "AppSettingsIndexEntry" | AppSettings["type"];
  allProblems: ProblemsIndexEntry[];
}>;

export type SourceFileIndexEntry = Readonly<{
  type: "SourceFileIndexEntry" | SourceFile["type"];
  path: string;
  isDirectory: boolean;
  allProblems: ProblemsIndexEntry[];
}>;

/**
 * This is a reference to any node that can report on it's problems. because there is no `types.any` we can't model a live reference (it could be one o many different types of nodes) so we use this ReferenceNode to collect the minimum information needed to allow the pageManager to open a page with the problem's node on it.
 */
export type ReferenceNode = Readonly<{
  type: string;
  key: string;
  apiIdentifier?: string;
  namespace?: string[];
  name?: string;
  parentKey?: string;
  parentApiIdentifier?: string;
  fieldType?: FieldType;
}>;

export type ActionIndexEntry = Readonly<{
  type: "ActionIndexEntry" | Instance<typeof Action>["type"];
  key: string;
  apiIdentifier: string;
  sourceFilePath: string;
}>;

export type ProblemsIndexEntry = Readonly<{
  messages: SnapshotIn<typeof ProblemMessage>[];
  node: ReferenceNode;
  subNodeKey?: string;
  nodeLabels?: ProblemIdentifier[];
}>;

export type ModelIndexEntry = Readonly<{
  type: "ModelIndexEntry" | DataModel["type"];
  key: string;
  /** @deprecated */
  name: string;
  color: string;
  schemaFilePath: string;
  apiIdentifier: string;
  namespace: string[];
  fieldsAsArray: FieldIndexEntry[];
  actions: ActionIndexEntry[];
  allProblems: ProblemsIndexEntry[];
  createdDate: number | Date;
  storageStrategy: {
    supportsConditionalPermissions: boolean;
    supportedFieldTypes: ReadonlyArray<FieldType>;
  };
}>;

export type FieldIndexEntry = Readonly<{
  type: "FieldIndexEntry" | ModelField["type"];
  key: string;
  apiIdentifier: string;
  fieldType: FieldType;
  configuration: {
    inverseFieldKey?: string | null;
    inverseJoinModelFieldKey?: string | null;
    inverseRelatedModelFieldKey?: string | null;
    includeTime?: boolean | null;
    sortable?: boolean | null;
  };
  contributorConfiguration: {
    [ShopifyMetafieldFieldContributorID]?: IShopifyMetafieldFieldContributorConfiguration;
  };
}>;

export type AuthenticationMethodIndexEntry = Readonly<{
  type: "AuthenticationMethodIndexEntry" | AuthenticationMethod["type"];
  key: string;
  name: string;
  allProblems: ProblemsIndexEntry[];
  createdDate: number | Date;
}>;
